import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";
import type { PermissionStatus } from "react-native-permissions";

type PermissionStatusOrUnknown = PermissionStatus | "unknown";

export const PermissionSwitch = observer(function PermissionSwitch({
  status,
  ...props
}: {
  status: PermissionStatusOrUnknown | undefined;
  unavailable:
    | undefined
    | ReactNode
    | { goto: Exclude<PermissionStatusOrUnknown, "unavailable"> };
  blocked:
    | undefined
    | ReactNode
    | { goto: Exclude<PermissionStatusOrUnknown, "blocked"> };
  denied:
    | undefined
    | ReactNode
    | { goto: Exclude<PermissionStatusOrUnknown, "denied"> };
  granted:
    | undefined
    | ReactNode
    | { goto: Exclude<PermissionStatusOrUnknown, "granted"> };
  limited:
    | undefined
    | ReactNode
    | { goto: Exclude<PermissionStatusOrUnknown, "limited"> };
  unknown:
    | undefined
    | ReactNode
    | { goto: Exclude<PermissionStatusOrUnknown, "unknown"> };
}) {
  const hasGoto = (value: unknown): value is { goto: unknown } =>
    typeof value === "object" && !!value && "goto" in value;

  const mapping = {
    unavailable: hasGoto(props.unavailable)
      ? props[props.unavailable.goto]
      : props.unavailable,
    blocked: hasGoto(props.blocked) ? props[props.blocked.goto] : props.blocked,
    denied: hasGoto(props.denied) ? props[props.denied.goto] : props.denied,
    granted: hasGoto(props.granted) ? props[props.granted.goto] : props.granted,
    limited: hasGoto(props.limited) ? props[props.limited.goto] : props.limited,
    unknown: hasGoto(props.unknown) ? props[props.unknown.goto] : props.unknown,
  };

  if (Object.values(mapping).some((value) => hasGoto(value))) {
    throw new Error("Circular dependency in PermissionSwitch");
  }

  if (status === "unavailable") {
    return mapping.unavailable as ReactNode;
  }

  if (status === "blocked") {
    return mapping.blocked as ReactNode;
  }

  if (status === "denied") {
    return mapping.denied as ReactNode;
  }

  if (status === "granted") {
    return mapping.granted as ReactNode;
  }

  if (status === "limited") {
    return mapping.limited as ReactNode;
  }

  if (status === "unknown") {
    return mapping.unknown as ReactNode;
  }

  return mapping.unknown as ReactNode;
});
